/**
 * Shared styling for AI actions — matches dashboard dark theme (#4ce1b6 accent).
 */

import { alpha } from '@mui/material/styles';
import { THEME_COLORS } from './theme';

export const AI_OVERLAY_ACCENT = THEME_COLORS.PRIMARY;
export const AI_OVERLAY_ACCENT_2 = '#0d9488';

export const AI_GRADIENT_BG = `linear-gradient(135deg, ${AI_OVERLAY_ACCENT_2} 0%, ${AI_OVERLAY_ACCENT} 100%)`;
export const AI_GRADIENT_BG_HOVER = `linear-gradient(135deg, #0f766e 0%, ${THEME_COLORS.PRIMARY_HOVER} 100%)`;
export const AI_GRADIENT_SHADOW = `0 4px 14px ${alpha(THEME_COLORS.PRIMARY, 0.35)}`;

/** MUI `sx` for contained AI trigger buttons */
export function aiGradientButtonSx(overrides = {}) {
  return {
    background: AI_GRADIENT_BG,
    color: '#0a0f1a',
    fontWeight: 700,
    textTransform: 'none',
    boxShadow: AI_GRADIENT_SHADOW,
    '&:hover': {
      background: AI_GRADIENT_BG_HOVER,
      boxShadow: AI_GRADIENT_SHADOW,
    },
    '&.Mui-disabled': {
      color: alpha('#0a0f1a', 0.45),
      background: `linear-gradient(135deg, ${alpha(AI_OVERLAY_ACCENT_2, 0.35)} 0%, ${alpha(AI_OVERLAY_ACCENT, 0.35)} 100%)`,
    },
    ...overrides,
  };
}

/** Full-area AI overlay shell — top-aligned by default; pass `centered: true` to vertically center. */
export function getAiOverlayShellSx(isDark, options = {}) {
  const { position = 'absolute', zIndex = 20, centered = false } = options;

  return {
    position,
    inset: 0,
    zIndex,
    display: 'flex',
    alignItems: centered ? 'center' : 'flex-start',
    justifyContent: 'center',
    pt: centered ? { xs: 1.5, sm: 2 } : { xs: 1.25, sm: 1.5 },
    px: { xs: 1.5, sm: 2 },
    pb: centered ? { xs: 1.5, sm: 2 } : 2,
    overflowY: 'auto',
    pointerEvents: 'auto',
    backgroundColor: alpha(isDark ? THEME_COLORS.BACKGROUND_DARK : '#f5f5f7', 0.78),
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
  };
}

export function getAiOverlayPaperSx(
  isDark,
  accent = AI_OVERLAY_ACCENT,
  accent2 = AI_OVERLAY_ACCENT_2,
) {
  return {
    position: 'relative',
    maxWidth: 500,
    width: '100%',
    maxHeight: { xs: 'calc(100% - 12px)', sm: 'calc(100% - 16px)' },
    overflow: 'auto',
    borderRadius: 3,
    flexShrink: 0,
    background: isDark
      ? `linear-gradient(165deg, ${THEME_COLORS.SURFACE_ELEVATED_DARK} 0%, ${THEME_COLORS.BACKGROUND_DARK} 100%)`
      : 'linear-gradient(165deg, #ffffff 0%, #f8fafc 100%)',
    border: `1px solid ${isDark ? alpha(accent, 0.22) : alpha(accent, 0.2)}`,
    boxShadow: isDark
      ? `0 24px 48px -12px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.04), 0 0 0 1px ${alpha(accent2, 0.08)}`
      : `0 24px 48px -12px ${alpha('#000', 0.12)}, 0 0 0 1px ${alpha(accent, 0.08)}`,
  };
}

export function getAiOverlayHeaderSx(accent = AI_OVERLAY_ACCENT, accent2 = AI_OVERLAY_ACCENT_2, isDark = true) {
  return {
    px: 2.5,
    py: 1.75,
    background: isDark
      ? `linear-gradient(90deg, ${alpha(accent, 0.14)} 0%, ${alpha(THEME_COLORS.SURFACE_HEADER_DARK, 0.98)} 55%, ${alpha(accent2, 0.08)} 100%)`
      : `linear-gradient(90deg, ${alpha(accent, 0.1)} 0%, ${alpha('#ffffff', 0.98)} 100%)`,
    borderBottom: `1px solid ${isDark ? alpha('#ffffff', 0.08) : alpha(accent, 0.15)}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 1,
  };
}

/** Rounded icon tile — matches distribution / channel setup section headers. */
export function getAiOverlayIconBoxSx(accent = AI_OVERLAY_ACCENT, size = 44) {
  return {
    width: size,
    height: size,
    borderRadius: '12px',
    bgcolor: alpha(accent, 0.12),
    border: `1px solid ${alpha(accent, 0.22)}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  };
}

export function getAiOverlayBodyTextSx(isDark) {
  return {
    color: isDark ? 'rgba(148, 163, 184, 0.92)' : '#64748b',
  };
}

export function getAiOverlayTitleSx(isDark) {
  return {
    color: isDark ? '#f8fafc' : '#0f172a',
    fontWeight: 700,
    letterSpacing: 0.2,
  };
}

export function getAiOverlayUploadButtonSx(isDark, accent = AI_OVERLAY_ACCENT) {
  return {
    borderColor: alpha(accent, isDark ? 0.45 : 0.35),
    color: accent,
    fontWeight: 600,
    textTransform: 'none',
    py: 1.1,
    borderWidth: 1.5,
    bgcolor: isDark ? alpha(accent, 0.06) : alpha(accent, 0.04),
    '&:hover': {
      borderWidth: 1.5,
      borderColor: accent,
      bgcolor: isDark ? alpha(accent, 0.12) : alpha(accent, 0.08),
    },
  };
}
